import { httpsCallable } from "firebase/functions";
import { functions, isProduction } from "./firebase/config";
import { readMarketingConsent } from "./utils/cookieConsent";
import { currentFbc, readFbpCookie } from "./utils/metaClick";

const PRODUCTION_COURSE = {
  orgId: "org_CrVtdTeRTZE4xs5FUBlnd1",
  programId: "nmXY4PIsPBgo0QyhiYNf",
};

const STAGING_COURSE = {
  orgId: "org_PbIXhUec2NgWJB2MGEQ3Dn",
  programId: "req797EF3VlXRx2BEuWT",
};

const course = isProduction ? PRODUCTION_COURSE : STAGING_COURSE;

export const PUBLIC_PROGRAM_ORG_ID = course.orgId;
export const PUBLIC_PROGRAM_ID = course.programId;

// Speed & Acceleration Course - one-time $60 USD purchase, from the new
// (blue) PEAK^ App Stripe account.
const STAGING_COURSE_PRICE_ID = "price_1UKphPS39eCdVnEKiur3hteg";
const PRODUCTION_COURSE_PRICE_ID = "price_1URqtZS8gobt3IzcmpzCVy0k";
export const COURSE_PRICE_ID = isProduction
  ? PRODUCTION_COURSE_PRICE_ID
  : STAGING_COURSE_PRICE_ID;

const FUNCTIONS_PROJECT_ID = isProduction ? "peak-x-937b6" : "peak-x-staging";
const FUNCTIONS_BASE_URL = `https://us-central1-${FUNCTIONS_PROJECT_ID}.cloudfunctions.net`;

export interface AcceptProgramResult {
  success: boolean;
  alreadyAssigned: boolean;
  programId: string;
  plannerProgramId: string | null;
  sessionCount: number;
  startDate: string;
}

const addAthleteToProgramFn = httpsCallable<
  { orgId: string; programId: string; startDate: string },
  AcceptProgramResult
>(functions, "addAthleteToProgram");

export async function acceptPublicProgram(
  orgId: string,
  programId: string,
  startDate: string,
): Promise<AcceptProgramResult> {
  const result = await addAthleteToProgramFn({ orgId, programId, startDate });
  return result.data;
}

export interface CoursePurchaseStatus {
  purchased: boolean;
  value?: number;
  currency?: string;
  eventId?: string;
}

const getCoursePurchaseStatusFn = httpsCallable<
  { programId: string; sessionId?: string },
  CoursePurchaseStatus
>(functions, "getCoursePurchaseStatus");

export async function fetchCoursePurchaseStatus(
  programId: string,
  sessionId?: string,
): Promise<CoursePurchaseStatus> {
  const result = await getCoursePurchaseStatusFn({ programId, sessionId });
  return result.data;
}

export function courseCheckoutUrls(origin: string): { successUrl: string; cancelUrl: string } {
  return {
    successUrl: `${origin}/home?checkout_session_id={CHECKOUT_SESSION_ID}`,
    cancelUrl: `${origin}/home`,
  };
}

export interface EnrollmentStatus {
  enrolled: boolean;
  startDate?: string | null;
  sessionCount?: number;
}

const getProgramEnrollmentStatusFn = httpsCallable<
  { orgId: string; programId: string },
  EnrollmentStatus
>(functions, "getProgramEnrollmentStatus");

export async function fetchProgramEnrollmentStatus(
  orgId: string,
  programId: string,
): Promise<EnrollmentStatus> {
  const result = await getProgramEnrollmentStatusFn({ orgId, programId });
  return result.data;
}

export async function startCourseCheckout(options: {
  orgId: string;
  programId: string;
  priceId: string;
  idToken: string;
  email: string | null;
}): Promise<string> {
  const { orgId, programId, priceId, idToken, email } = options;
  const { successUrl, cancelUrl } = courseCheckoutUrls(window.location.origin);
  const marketingConsent = readMarketingConsent();
  const response = await fetch(`${FUNCTIONS_BASE_URL}/createCourseCheckoutSession`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      priceId,
      orgId,
      programId,
      email,
      successUrl,
      cancelUrl,
      marketingConsent,
      ...(marketingConsent
        ? {
            fbp: readFbpCookie(),
            fbc: currentFbc(),
            sourceUrl: window.location.href.split("#")[0],
          }
        : {}),
    }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || "Couldn't start checkout");
  }
  const { url } = await response.json();
  return url;
}
